import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { cn } from "../utils/cx.js";
import { Field } from "./Field.js";
import { useLocale } from "../locale/LocaleProvider.js";
import {
  clampCanonical,
  currencyAffix,
  formatCanonical,
  localeSeparators,
  stepCanonical,
  toCanonical,
} from "../utils/number.js";

export interface NumberInputProps {
  /** Canonical decimal string ("." decimal, no grouping). Controlled. */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Override the LocaleProvider locale / currency. */
  locale?: string;
  currency?: string;
  /** Max fraction digits; also pads to this on blur. */
  decimals?: number;
  min?: number;
  max?: number;
  step?: number;
  allowNegative?: boolean;
  grouping?: boolean;
  showSteppers?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  size?: "sm" | "md" | "lg";
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  onBlur?: () => void;
  onFocus?: () => void;
}

const HEIGHT = { sm: "h-8", md: "h-9", lg: "h-10" } as const;

/**
 * The canonical-decimal engine (locale-aware typing/formatting) is kept custom;
 * only the visual box renders as a shadcn-style input shell so it matches the
 * other fields. Numeric extras (right-align, steppers) live in components.css.
 */
export function NumberInput(props: NumberInputProps) {
  const ctx = useLocale();
  const locale = props.locale ?? ctx.locale;
  const currency = props.currency;
  const {
    decimals,
    min,
    max,
    step = 1,
    allowNegative = true,
    grouping = true,
    showSteppers = false,
    size = "md",
    disabled,
    readOnly,
  } = props;

  const auto = useId();
  const inputId = props.id ?? auto;
  const isControlled = props.value !== undefined;
  const [inner, setInner] = useState(props.defaultValue ?? "");
  const canonical = isControlled ? (props.value ?? "") : inner;

  const [focused, setFocused] = useState(false);
  const [display, setDisplay] = useState(() =>
    formatCanonical(canonical, { locale, decimals, grouping, padFraction: true }),
  );
  const sep = useMemo(() => localeSeparators(locale), [locale]);

  useEffect(() => {
    if (!focused) {
      setDisplay(formatCanonical(canonical, { locale, decimals, grouping, padFraction: true }));
    }
  }, [canonical, focused, locale, decimals, grouping]);

  const emit = (next: string) => {
    if (!isControlled) setInner(next);
    props.onChange?.(next);
  };

  const sanitizeTyped = (raw: string): string => {
    const neg = allowNegative && raw.includes("-");
    let s = raw.replace(new RegExp(`[^0-9${sep.decimal === "." ? "\\." : sep.decimal}]`, "g"), "");
    const di = s.indexOf(sep.decimal);
    if (di !== -1) {
      const head = s.slice(0, di + 1);
      const tail = s
        .slice(di + 1)
        .split(sep.decimal)
        .join("");
      s = head + (decimals != null ? tail.slice(0, decimals) : tail);
    }
    return (neg ? "-" : "") + s;
  };

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const typed = sanitizeTyped(e.target.value);
    setDisplay(typed);
    emit(toCanonical(typed, locale, allowNegative));
  };

  const commit = () => {
    let c = canonical;
    if (c.endsWith(".")) c = c.slice(0, -1);
    if (c === "-" || c === "") c = "";
    c = clampCanonical(c, min, max);
    emit(c);
    setFocused(false);
    setDisplay(formatCanonical(c, { locale, decimals, grouping, padFraction: true }));
  };

  const doStep = (dir: 1 | -1) => {
    if (disabled || readOnly) return;
    const next = stepCanonical(canonical, dir * step, decimals, min, max);
    emit(next);
    setDisplay(formatCanonical(next, { locale, decimals, grouping: !focused, padFraction: !focused }));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      doStep(1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      doStep(-1);
    }
  };

  const num = canonical === "" || canonical === "-" ? NaN : Number(canonical);
  const atMax = max != null && !Number.isNaN(num) && num >= max;
  const atMin = min != null && !Number.isNaN(num) && num <= min;

  const affix = currency ? currencyAffix(locale, currency) : null;
  const prefixNode = props.prefix ?? (affix?.prefix ? affix.symbol : undefined);
  const suffixNode = props.suffix ?? (affix && !affix.prefix ? affix.symbol : undefined);
  const invalid = props.error != null;

  const box = (
    <div
      className={cn(
        "flex w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] dark:bg-input/30",
        HEIGHT[size],
        invalid && "border-destructive focus-within:border-destructive focus-within:ring-destructive/30",
        disabled && "opacity-50",
        props.className,
      )}
    >
      {prefixNode != null && <span className="shrink-0 text-muted-foreground">{prefixNode}</span>}
      <input
        id={inputId}
        name={props.name}
        className="az-number h-full w-full bg-transparent text-right tabular-nums outline-none rtl:text-left placeholder:text-muted-foreground"
        type="text"
        inputMode={decimals === 0 ? "numeric" : "decimal"}
        autoComplete="off"
        role="spinbutton"
        aria-valuenow={Number.isNaN(num) ? undefined : num}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={props.placeholder}
        value={display}
        onChange={onInput}
        onKeyDown={onKeyDown}
        onFocus={() => {
          setFocused(true);
          setDisplay(formatCanonical(canonical, { locale, decimals, grouping: false }));
          props.onFocus?.();
        }}
        onBlur={() => {
          commit();
          props.onBlur?.();
        }}
      />
      {suffixNode != null && <span className="shrink-0 text-muted-foreground">{suffixNode}</span>}
      {showSteppers && !readOnly && (
        <span className="az-number__steppers" aria-hidden>
          <button
            type="button"
            tabIndex={-1}
            className="az-number__step"
            disabled={disabled || atMax}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => doStep(1)}
          >
            ▲
          </button>
          <button
            type="button"
            tabIndex={-1}
            className="az-number__step"
            disabled={disabled || atMin}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => doStep(-1)}
          >
            ▼
          </button>
        </span>
      )}
    </div>
  );

  if (props.label != null || props.help != null || props.error != null) {
    return (
      <Field
        label={props.label}
        required={props.required}
        help={props.help}
        error={props.error}
        htmlFor={inputId}
      >
        {box}
      </Field>
    );
  }
  return box;
}
